import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
    ChatBubbleLeftRightIcon,
    XMarkIcon,
    PaperAirplaneIcon,
    SparklesIcon,
    AcademicCapIcon,
    DocumentDuplicateIcon,
    ArrowPathIcon,
    TrashIcon
} from '@heroicons/react/24/outline';
import { useCurrentUser } from '../UserProvider/UserProvider';
import axios from 'axios';
import { baseUrl } from '../shared';

import { useLocation, useNavigate } from 'react-router-dom';

const AIAgent = () => {
    const { user, loggedIn } = useCurrentUser();
    const [isOpen, setIsOpen] = useState(false);
    const [unreadWormieMessage, setUnreadWormieMessage] = useState(null);
    const [message, setMessage] = useState('');
    const location = useLocation();
    const navigate = useNavigate();
    const [isMobile, setIsMobile] = useState(window.innerWidth < 640);
    const [showTooltip, setShowTooltip] = useState(true);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 640);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);
    const [activeTargetUser, setActiveTargetUser] = useState(() => {
        const stored = sessionStorage.getItem('activeTargetUser');
        return stored ? JSON.parse(stored) : null;
    });

    const [chatHistory, setChatHistory] = useState([
        {
            role: 'assistant',
            content: user?.role === 'college_staff'
                ? "Hello! I'm Wormie, your institutional development consultant. I'm analyzing your candidate pools and recruitment matches right now..."
                : "Hello! I'm Wormie, your personal college admissions assistant. I'm analyzing your profile to give you the best advice..."
        }
    ]);
    const [isThinking, setIsThinking] = useState(false);
    const chatEndRef = useRef(null);
    const abortControllerRef = useRef(null);
    const lastUserMessageRef = useRef('');
    const [lastError, setLastError] = useState(null);

    const scrollToBottom = () => {
        chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    // Draggable Logic - Refactored to use offsets for reliable corner anchoring
    const [offset, setOffset] = useState(() => {
        const stored = localStorage.getItem('wormieOffset');
        if (stored) {
            try {
                const parsed = JSON.parse(stored);
                // Validate structure: must have right/bottom. If it has x/y (old version), migrate it or reset.
                if (parsed && typeof parsed.right === 'number' && typeof parsed.bottom === 'number') {
                    return parsed;
                }
            } catch (e) {}
        }
        
        // Reset to corner on first load or if malformed
        return { right: 20, bottom: 20 };
    });
    const [isDragging, setIsDragging] = useState(false);
    const dragStartPos = useRef({ startX: 0, startY: 0, initialRight: 0, initialBottom: 0 });
    const dragDistance = useRef(0);

    const handleStartDrag = useCallback((e) => {
        setIsDragging(true);
        const clientX = e.type === 'touchstart' ? e.touches[0].clientX : e.clientX;
        const clientY = e.type === 'touchstart' ? e.touches[0].clientY : e.clientY;

        dragStartPos.current = {
            startX: clientX,
            startY: clientY,
            initialRight: offset.right,
            initialBottom: offset.bottom
        };
        dragDistance.current = 0;
    }, [offset]);

    const handleDragMove = useCallback((e) => {
        if (!isDragging) return;

        const clientX = e.type === 'touchmove' ? e.touches[0].clientX : e.clientX;
        const clientY = e.type === 'touchmove' ? e.touches[0].clientY : e.clientY;

        const dx = clientX - dragStartPos.current.startX;
        const dy = clientY - dragStartPos.current.startY;
        dragDistance.current = Math.sqrt(dx * dx + dy * dy);

        // Invert delta: moving left (negative dx) should increase 'right' offset
        const newRight = dragStartPos.current.initialRight - dx;
        const newBottom = dragStartPos.current.initialBottom - dy;

        // Dynamic boundaries based on open state
        const padding = 10;
        const chatW = window.innerWidth < 640 ? window.innerWidth * 0.92 : 420;
        const chatH = window.innerWidth < 640 ? window.innerHeight * 0.75 : 550;
        
        const maxR = window.innerWidth - padding - (isOpen ? chatW : 80);
        const maxB = window.innerHeight - padding - (isOpen ? chatH : 80);

        setOffset({
            right: Math.max(padding, Math.min(maxR, newRight)),
            bottom: Math.max(padding, Math.min(maxB, newBottom))
        });
        
        if (e.type === 'touchmove') e.preventDefault();
    }, [isDragging, isOpen]);

    const handleStopDrag = useCallback(() => {
        if (!isDragging) return;
        setIsDragging(false);
        localStorage.setItem('wormieOffset', JSON.stringify(offset));
    }, [isDragging, offset]);

    useEffect(() => {
        if (isDragging) {
            window.addEventListener('mousemove', handleDragMove);
            window.addEventListener('mouseup', handleStopDrag);
            window.addEventListener('touchmove', handleDragMove, { passive: false });
            window.addEventListener('touchend', handleStopDrag);
        }
        return () => {
            window.removeEventListener('mousemove', handleDragMove);
            window.removeEventListener('mouseup', handleStopDrag);
            window.removeEventListener('touchmove', handleDragMove);
            window.removeEventListener('touchend', handleStopDrag);
        };
    }, [isDragging, handleDragMove, handleStopDrag]);

    useEffect(() => {
        if (isOpen) {
            setTimeout(() => {
                scrollToBottom();
            }, 50);
        }
    }, [isOpen, chatHistory, isThinking]);

    // Keep bubble in bounds during resize
    useEffect(() => {
        const checkBounds = () => {
            const padding = 10;
            const chatWidth = window.innerWidth < 640 ? window.innerWidth * 0.95 : 420;
            const chatHeight = window.innerWidth < 640 ? window.innerHeight * 0.85 : 550;
            
            const maxR = window.innerWidth - padding - (isOpen ? chatWidth : 80);
            const maxB = window.innerHeight - padding - (isOpen ? chatHeight : 80);

            setOffset(prev => {
                const currentR = (typeof prev.right === 'number') ? prev.right : 20;
                const currentB = (typeof prev.bottom === 'number') ? prev.bottom : 20;
                return {
                    right: Math.max(padding, Math.min(currentR, maxR)),
                    bottom: Math.max(padding, Math.min(currentB, maxB))
                };
            });
        };
        window.addEventListener('resize', checkBounds);
        checkBounds();
        return () => window.removeEventListener('resize', checkBounds);
    }, [isOpen]);

    const notifiedMatchesRef = useRef(new Set(
        JSON.parse(sessionStorage.getItem('notifiedMatches') || '[]')
    ));

    useEffect(() => {
        if (!loggedIn || !user) return;

        const pingOnlineStatus = async () => {
            try {
                const token = localStorage.getItem('access');
                if (!token) return;

                const response = await axios.post(`${baseUrl}api/ping-online/`, {}, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });

                if (response.data && response.data.matches) {
                    const newMatches = response.data.matches.filter(m => !notifiedMatchesRef.current.has(m.user_id));

                    if (newMatches.length > 0) {
                        newMatches.forEach(m => notifiedMatchesRef.current.add(m.user_id));
                        sessionStorage.setItem('notifiedMatches', JSON.stringify(Array.from(notifiedMatchesRef.current)));

                        // Save the last pinged user as the active target for any drafted messages
                        const targetUserObj = {
                            id: newMatches[newMatches.length - 1].user_id,
                            name: newMatches[newMatches.length - 1].name
                        };
                        setActiveTargetUser(targetUserObj);
                        sessionStorage.setItem('activeTargetUser', JSON.stringify(targetUserObj));

                        const aiMessages = newMatches.map(m => {
                            if (user?.role === 'college_staff') {
                                return `Hi! A prospective student, **${m.name}**, who bookmarked your college is currently online. Would you like me to draft a message for them?`;
                            } else {
                                return `Hello! I noticed that a representative from **${m.college_name}** (which you bookmarked) is online! Their name is ${m.name}. Exploring your options is important, would you like to start a chat with them?`;
                            }
                        });

                        setChatHistory(prev => [
                            ...prev,
                            ...aiMessages.map(msg => ({ role: 'assistant', content: msg }))
                        ]);

                        // Set the thought bubble to the first message if it's closed,
                        // otherwise just let it scroll down inside the open chat
                        if (!isOpen) {
                            setUnreadWormieMessage(aiMessages[0]);
                        }
                    }
                }
            } catch (error) {
                console.error("Error pinging online status:", error);
            }
        };

        // Ping immediately on mount/login, then every 30 seconds
        pingOnlineStatus();
        const intervalId = setInterval(pingOnlineStatus, 30000);

        return () => clearInterval(intervalId);
    }, [loggedIn, user]);

    // Load chat history on mount/login
    useEffect(() => {
        if (loggedIn) {
            const fetchHistory = async () => {
                try {
                    const token = localStorage.getItem('access');
                    if (!token) return;

                    const response = await axios.get(`${baseUrl}api/ai/history/`, {
                        headers: { 'Authorization': `Bearer ${token}` }
                    });

                    if (response.data && response.data.length > 0) {
                        // Map roles: 'model' -> 'assistant'
                        const history = response.data.map(m => ({
                            role: m.role === 'model' ? 'assistant' : 'user',
                            content: m.content
                        }));
                        setChatHistory(history);
                    }
                } catch (error) {
                    console.error("Error fetching chat history", error);
                }
            };
            fetchHistory();
        } else {
            // Reset to default greeting if logged out
            setChatHistory([
                {
                    role: 'assistant',
                    content: user?.role === 'college_staff'
                        ? "Hello! I'm Wormie, your institutional development consultant. I'm analyzing your candidate pools and recruitment matches right now..."
                        : "Hello! I'm Wormie, your personal college admissions assistant. I'm analyzing your profile to give you the best advice..."
                }
            ]);
        }
    }, [loggedIn, user?.role]); // Re-trigger when login state changes

    const handleVisualClear = (e) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }
        setChatHistory([
            {
                role: 'assistant',
                content: user?.role === 'college_staff'
                    ? "Screen cleared! I still remember our conversation context. How can I help you further?"
                    : "Screen cleared! I'm ready for your next question."
            }
        ]);
    };

    const handleClearHistory = async (e) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }

        try {
            const token = localStorage.getItem('access');
            if (token && token !== 'null') {
                await axios.delete(`${baseUrl}api/ai/history/`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
            }
        } catch (error) {
            console.error("Error clearing history on backend", error);
        } finally {
            // Always clear the UI state, even if the backend delete fails
            setChatHistory([
                {
                    role: 'assistant',
                    content: "Your chat history has been permanently deleted! How can I help you next?"
                }
            ]);
        }
    };

    const handleStopChat = () => {
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
            abortControllerRef.current = null;
        }
        setIsThinking(false);
        setChatHistory(prev => {
            const last = prev[prev.length - 1];
            if (last && last.role === 'assistant' && last.content === "") {
                return prev.slice(0, -1); // Remove empty assistant bubble if it was just starting
            }
            return prev;
        });
    };

    const handleRetryChat = () => {
        if (lastUserMessageRef.current) {
            setMessage(lastUserMessageRef.current);
            setLastError(null);
            // We'll let the user click send or we could trigger it automatically. 
            // The request was for a retry OPTION, so let's just populate the input 
            // for them and they can click send, or we trigger it.
            // Triggering it is better for UX.
            // But handleSendMessage needs an event. Let's make a wrapper.
        }
    };

    const handleSendMessage = useCallback(async (e, retryMessage = null) => {
        if (e) e.preventDefault();
        
        const messageToSend = retryMessage || message.trim();
        if (!messageToSend || isThinking) return;

        const userMessage = messageToSend;
        lastUserMessageRef.current = userMessage;
        setMessage('');
        setLastError(null);

        // Only add to history if it's NOT a retry being added again (or just follow normal flow)
        if (!retryMessage) {
            setChatHistory(prev => [...prev, { role: 'user', content: userMessage }]);
        }
        
        setIsThinking(true);

        // Initialize AbortController
        abortControllerRef.current = new AbortController();

        try {
            const token = localStorage.getItem('access');
            const headers = {
                'Content-Type': 'application/json',
                ...(token ? { 'Authorization': `Bearer ${token}` } : {})
            };

            const response = await fetch(`${baseUrl}api/ai/chat/`, {
                method: 'POST',
                headers: headers,
                signal: abortControllerRef.current.signal,
                body: JSON.stringify({
                    message: userMessage,
                    context: {
                        path: location.pathname
                    },
                    history: chatHistory.slice(-5).map(msg => ({
                        role: msg.role === 'assistant' ? 'model' : 'user',
                        parts: [msg.content]
                    }))
                })
            });

            if (!response.ok) throw new Error("Failed to connect to AI");

            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            let done = false;
            let fullReply = "";

            while (!done) {
                const { value, done: doneReading } = await reader.read();
                done = doneReading;
                const chunkValue = decoder.decode(value, { stream: !done });

                if (chunkValue) {
                    if (fullReply === "") {
                        setIsThinking(false);
                        setChatHistory(prev => [...prev, { role: 'assistant', content: chunkValue }]);
                    } else {
                        setChatHistory(prev => {
                            const newHistory = [...prev];
                            newHistory[newHistory.length - 1] = {
                                role: 'assistant',
                                content: fullReply + chunkValue
                            };
                            return newHistory;
                        });
                    }
                    fullReply += chunkValue;
                }
            }

        } catch (error) {
            if (error.name === 'AbortError') {
                console.log('Chat aborted');
                setChatHistory(prev => [...prev, { role: 'assistant', content: "(Conversation stopped.)" }]);
            } else {
                console.error('Wormie Chat Error:', error);
                setLastError(error.message);
                setChatHistory(prev => {
                    const newHistory = [...prev];
                    return [...newHistory, {
                        role: 'assistant',
                        content: `I'm having a little trouble connecting to my knowledge base (Error: ${error.message}). Please try again in a moment!`
                    }];
                });
            }
        } finally {
            setIsThinking(false);
            abortControllerRef.current = null;
        }
    }, [message, isThinking, chatHistory, location.pathname]);

    useEffect(() => {
        const handleBookingSuccess = (e) => {
            const { advisorName, scheduledAt } = e.detail;
            setIsOpen(true);
            setUnreadWormieMessage(null);
            setShowTooltip(false);

            const queryText = `I just successfully scheduled a consultation session with Advisor ${advisorName} on ${new Date(scheduledAt).toLocaleString()}! Can you confirm my schedule and give me tips on how to prepare?`;
            
            setTimeout(() => {
                handleSendMessage(null, queryText);
            }, 600);
        };

        window.addEventListener('wormie-booking-success', handleBookingSuccess);
        return () => {
            window.removeEventListener('wormie-booking-success', handleBookingSuccess);
        };
    }, [handleSendMessage]);


    return (
        <div
            className={`fixed z-[9999] flex flex-col transition-all duration-300 ${isOpen && isMobile ? 'inset-0 items-center justify-center p-4 bg-black/40 backdrop-blur-[10px] pointer-events-auto' : 'items-end pointer-events-none'}`}
            style={(isOpen && isMobile)
                ? {} 
                : { right: offset.right || 20, bottom: offset.bottom || 20 }
            }
        >
            {/* Chat Window */}
            {isOpen && (
                <div 
                    className={`w-full max-w-[420px] ${isMobile ? 'h-[85vh]' : 'h-[550px] mb-4'} bg-white/95 backdrop-blur-3xl rounded-[2.5rem] shadow-2xl border border-white/40 overflow-hidden flex flex-col animate-slideUp pointer-events-auto cursor-default`}
                >
                    {/* Header - Draggable Hub */}
                    <div
                        onMouseDown={handleStartDrag}
                        onTouchStart={handleStartDrag}
                        className="p-6 bg-gray-800 text-white flex items-center justify-between border-b border-white/10 cursor-move active:cursor-grabbing select-none"
                    >
                        <div className="flex items-center gap-3 pointer-events-none">
                            <div className="p-2 bg-[#A855F7] rounded-2xl">
                                <img src="/wormie-logo.svg" alt="Wormie" className="w-8 h-8" />
                            </div>
                            <div className="min-w-0">
                                <h3 className="font-bold text-lg tracking-tight">Wormie</h3>
                                <div className="flex items-center gap-1.5">
                                    <span className="w-1.5 h-1.5 bg-green-400 rounded-full animate-pulse shadow-[0_0_8px_rgba(74,222,128,0.8)]"></span>
                                    <span className="text-[10px] text-white/70 font-bold uppercase tracking-widest truncate"> Admissions Agent</span>
                                </div>
                            </div>
                        </div>
                        <div className="flex items-center gap-1">
                            {loggedIn && (
                                <div className="flex items-center mr-1 bg-white/5 rounded-2xl p-0.5">
                                    <button
                                        onClick={handleVisualClear}
                                        className="p-2 hover:bg-white/10 rounded-xl transition-colors text-white/50 hover:text-white"
                                        title="Clear Screen"
                                    >
                                        <ArrowPathIcon className="w-4 h-4" />
                                    </button>
                                    <button
                                        onClick={handleClearHistory}
                                        className="p-2 hover:bg-white/10 rounded-xl transition-colors text-white/50 hover:text-red-400"
                                        title="Delete Memory"
                                    >
                                        <TrashIcon className="w-4 h-4" />
                                    </button>
                                </div>
                            )}
                            <button
                                onClick={() => setIsOpen(false)}
                                className="p-2 hover:bg-white/10 rounded-xl transition-colors"
                            >
                                <XMarkIcon className="w-6 h-6" />
                            </button>
                        </div>
                    </div>

                    {/* Messages Area */}
                    <div className="flex-1 overflow-y-auto p-6 space-y-4 custom-scrollbar bg-gray-50/30">
                        {chatHistory.map((chat, index) => (
                            <div
                                key={index}
                                className={`flex w-full ${chat.role === 'user' ? 'justify-end' : 'justify-start'}`}
                            >
                                <div className={`max-w-[85%] p-4 rounded-2xl text-sm leading-relaxed ${chat.role === 'user'
                                    ? 'bg-[#A855F7] text-white rounded-tr-none shadow-lg shadow-purple-500/20'
                                    : 'bg-white text-gray-800 rounded-tl-none border border-gray-200 shadow-sm'
                                    }`}>
                                    {(() => {
                                        // Parse and extract any action tags
                                        const actionRegex = /\[\[ACTION:\s*(\w+),\s*College:\s*([^\]]+)\]\]/gi;
                                        const actions = [];
                                        let match;
                                        let cleanContent = chat.content;
                                        
                                        // Store all actions
                                        while ((match = actionRegex.exec(chat.content)) !== null) {
                                            actions.push({ type: match[1].toUpperCase(), college: match[2].trim() });
                                        }
                                        
                                        // Strip the actions from text output so they are hidden from user
                                        cleanContent = cleanContent.replace(actionRegex, '').trim();

                                        return (
                                            <div className="space-y-3">
                                                <div>
                                                    {cleanContent.split('---').map((block, blockIndex) => {
                                                        const isDraft = blockIndex % 2 !== 0;
                                                        let draftContent = block;
                                                        let extractedTarget = null;

                                                        if (isDraft) {
                                                            const targetMatch = draftContent.match(/\[TARGET_ID:\s*([a-zA-Z0-9_]+)\|([^\]]+)\]/);
                                                            if (targetMatch) {
                                                                extractedTarget = { id: targetMatch[1], name: targetMatch[2].trim() };
                                                                draftContent = draftContent.replace(targetMatch[0], '').trim();
                                                            } else {
                                                                extractedTarget = activeTargetUser;
                                                            }
                                                        }

                                                        return (
                                                            <div key={blockIndex} className={isDraft ? "my-3 p-3 bg-purple-50/50 border border-purple-200/60 rounded-xl relative" : ""}>
                                                                {draftContent.split('\n').map((line, i) => (
                                                                    <React.Fragment key={i}>
                                                                        {line.split(/(\*\*.*?\*\*)/g).map((part, j) => {
                                                                            if (part.startsWith('**') && part.endsWith('**')) {
                                                                                return <strong key={j} className="font-bold">{part.slice(2, -2)}</strong>;
                                                                            }
                                                                            return <span key={j}>{part}</span>;
                                                                        })}
                                                                        {i < draftContent.split('\n').length - 1 && <br />}
                                                                    </React.Fragment>
                                                                ))}
                                                                {isDraft && (
                                                                    <button
                                                                        onClick={() => {
                                                                            setIsOpen(false);
                                                                            navigate('/messages', {
                                                                                state: {
                                                                                    draftText: draftContent.trim(),
                                                                                    ...(extractedTarget && extractedTarget.id && extractedTarget.id !== "undefined" ? {
                                                                                        openChatWithUserId: extractedTarget.id,
                                                                                        openChatWithUserName: extractedTarget.name
                                                                                    } : {})
                                                                                }
                                                                            });
                                                                        }}
                                                                        className="mt-3 w-full bg-[#A855F7] hover:bg-purple-600 text-white font-bold py-2 rounded-lg text-xs shadow-md transition-all flex items-center justify-center gap-2"
                                                                    >
                                                                        <ChatBubbleLeftRightIcon className="w-4 h-4" />
                                                                        Open in Direct Messages
                                                                    </button>
                                                                )}
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                                
                                                {/* Render action badges below content */}
                                                {actions.length > 0 && (
                                                    <div className="space-y-1.5 mt-2 pt-2 border-t border-gray-100">
                                                        {actions.map((act, i) => (
                                                            <div key={i} className="flex items-center gap-2 bg-emerald-50 text-emerald-700 p-2.5 rounded-xl text-xs font-bold border border-emerald-200">
                                                                <span>{act.type === 'BOOKMARK' ? '🎓' : '📢'}</span>
                                                                <span>
                                                                    {act.type === 'BOOKMARK' 
                                                                        ? `Added ${act.college} to your Bookmarks!` 
                                                                        : `Submitted recruitment profile to ${act.college}!`}
                                                                </span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })()}
                                </div>
                            </div>
                        ))}
                        {isThinking && (
                            <div className="flex flex-col items-start gap-2">
                                <div className="bg-white border border-gray-100 p-4 rounded-3xl rounded-tl-none shadow-sm">
                                    <div className="flex gap-1.5 ">
                                        <div className="w-2 h-2 bg-[#A855F7]/40 rounded-full animate-bounce"></div>
                                        <div className="w-2 h-2 bg-[#A855F7]/40 rounded-full animate-bounce [animation-delay:0.2s]"></div>
                                        <div className="w-2 h-2 bg-[#A855F7]/40 rounded-full animate-bounce [animation-delay:0.4s]"></div>
                                    </div>
                                </div>
                            </div>
                        )}
                        {!isThinking && lastError && (
                             <div className="flex justify-center py-2">
                             <button
                                 onClick={() => handleSendMessage(null, lastUserMessageRef.current)}
                                 className="flex items-center gap-2 px-4 py-2 bg-purple-50 hover:bg-purple-100 text-[#A855F7] rounded-xl text-xs font-bold transition-all border border-purple-200/50"
                             >
                                 <ArrowPathIcon className="w-4 h-4" /> Retry Last Message
                             </button>
                         </div>
                        )}
                        <div ref={chatEndRef} />
                    </div>

                    {/* Input Area */}
                    <form onSubmit={handleSendMessage} className="p-5 bg-white border-t border-gray-100 flex gap-3 items-center">
                        <input
                            type="text"
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                            placeholder="Type your message..."
                            className="flex-1 bg-gray-50/80 border-none rounded-2xl px-5 py-3 text-sm focus:ring-2 focus:ring-purple/20 transition-all outline-none text-gray-700 font-medium"
                        />
                        {isThinking && (
                            <button
                                type="button"
                                onClick={handleStopChat}
                                title="Stop generating"
                                className="w-[44px] h-[44px] flex items-center justify-center bg-red-50 hover:bg-red-100 rounded-2xl transition-all flex-shrink-0 border border-red-200"
                            >
                                <div className="w-3.5 h-3.5 bg-red-500 rounded-sm"></div>
                            </button>
                        )}
                        <button
                            type="submit"
                            disabled={!message.trim() || isThinking}
                            className="p-3 bg-black text-white rounded-2xl hover:bg-black/80 disabled:opacity-50 disabled:cursor-not-allowed transition-all active:scale-95 flex-shrink-0 shadow-lg shadow-black/30 w-[44px] h-[44px] flex items-center justify-center"
                        >
                            <PaperAirplaneIcon className="w-5 h-5 -ml-0.5" />
                        </button>
                    </form>
                </div>
            )}

            {/* Floating Open Button - Only shows when closed */}
            {!isOpen && (
                <div className="relative group">
                    {/* Unread thought bubble or descriptive tooltip */}
                    {(unreadWormieMessage || showTooltip) && (
                        <div className="absolute right-full mr-4 top-1/2 -translate-y-1/2 transform transition-all duration-300 pointer-events-auto">
                            <div
                                onClick={() => {
                                    setIsOpen(true);
                                    setUnreadWormieMessage(null);
                                    setShowTooltip(false);
                                }}
                                className="bg-white px-4 py-3 rounded-2xl rounded-tr-sm shadow-xl border border-gray-100 min-w-[140px] max-w-[280px] cursor-pointer hover:bg-gray-50 transition-colors animate-[pulse_3s_ease-in-out_infinite] group-hover:scale-105 relative"
                            >
                                {/* Dismiss Button */}
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setShowTooltip(false);
                                        setUnreadWormieMessage(null);
                                    }}
                                    className="absolute -top-2 -left-2 w-5 h-5 bg-gray-100 hover:bg-gray-200 text-gray-400 hover:text-gray-600 rounded-full flex items-center justify-center shadow-sm border border-gray-200 transition-colors z-20"
                                    title="Dismiss"
                                >
                                    <XMarkIcon className="w-3 h-3" />
                                </button>

                                <div className="flex items-center gap-2">
                                    <div className="p-1 bg-purple/10 rounded-lg">
                                        <SparklesIcon className="w-4 h-4 text-purple" />
                                    </div>
                                    <p className="text-sm font-bold text-gray-800 whitespace-nowrap">
                                        {unreadWormieMessage ? "Message from Wormie" : "Ask Wormie anything!"}
                                    </p>
                                </div>
                                {unreadWormieMessage && (
                                    <p className="text-xs text-gray-500 mt-1 line-clamp-2 font-medium">
                                        {unreadWormieMessage}
                                    </p>
                                )}
                                {/* Triangle pointer */}
                                <div className="absolute top-1/2 right-[-6px] -translate-y-1/2 w-3 h-3 bg-white border-r border-t border-gray-100 rotate-45"></div>
                            </div>
                        </div>
                    )}

                    <button
                        onMouseDown={handleStartDrag}
                        onTouchStart={handleStartDrag}
                        onClick={(e) => {
                            // Only open if we haven't moved more than a few pixels (i.e., it was a click)
                            if (dragDistance.current < 10) {
                                setIsOpen(true);
                                setUnreadWormieMessage(null);
                                setShowTooltip(false);
                            }
                        }}
                        className={`group relative p-5 rounded-full shadow-2xl transition-all duration-300 hover:scale-110 active:scale-90 flex items-center justify-center pointer-events-auto bg-[#A855F7] text-white border border-white/10 ${unreadWormieMessage ? 'animate-[bounce_2s_infinite]' : ''} ${isDragging ? 'cursor-grabbing scale-110' : 'cursor-grab'}`}
                    >
                        <div className={`absolute inset-0 rounded-full bg-purple opacity-20 group-hover:opacity-40 ${unreadWormieMessage ? 'animate-ping' : ''}`}></div>
                        <img src="/wormie-logo.svg" alt="Wormie" className="w-10 h-10 relative z-10 pointer-events-none" />

                        {/* Red dot indicator if there's a message and bubble is hidden for some reason */}
                        {unreadWormieMessage && (
                            <div className="absolute top-0 right-0 w-4 h-4 bg-red-500 rounded-full border-2 border-[#A855F7] shadow-sm z-20"></div>
                        )}
                    </button>
                </div>
            )}
        </div>
    );
};

export default AIAgent;
